import { clearAnalyticsUserId, trackLogout } from "./analytics";

const TOKEN_KEY = "kaidoki_token";
const USER_KEY = "kaidoki_user";
const USER_ID_KEY = "kaidoki_user_id";

export interface UserInfo {
  displayName: string;
  pictureUrl: string | null;
  isPro: boolean;
  authProviders: string[];
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function getUserId(): string | null {
  return localStorage.getItem(USER_ID_KEY);
}

export function setUserId(userId: string): void {
  localStorage.setItem(USER_ID_KEY, userId);
}

export function getUserInfo(): UserInfo | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserInfo;
  } catch {
    return null;
  }
}

export function setUserInfo(info: UserInfo): void {
  localStorage.setItem(USER_KEY, JSON.stringify(info));
}

export function isLoggedIn(): boolean {
  return getToken() !== null;
}

export async function logout(): Promise<void> {
  trackLogout();
  clearAnalyticsUserId();

  const token = getToken();
  if (token) {
    await fetch("/api/v0/auth/logout", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  }
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(USER_ID_KEY);

  // Firebase sign out (dynamic import to minimize bundle impact)
  try {
    const { firebaseSignOut } = await import("./firebase-auth");
    await firebaseSignOut();
  } catch {
    // Firebase not initialized or sign out failed — ignore
  }
}

export function generateState(redirect?: string): string {
  const nonce = Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  localStorage.setItem("kaidoki_oauth_state", nonce);
  if (redirect) {
    return `${nonce}.${btoa(redirect)}`;
  }
  return nonce;
}

export function verifyState(state: string): {
  valid: boolean;
  redirect: string | null;
} {
  const saved = localStorage.getItem("kaidoki_oauth_state");
  localStorage.removeItem("kaidoki_oauth_state");
  const dotIndex = state.indexOf(".");
  if (dotIndex === -1) {
    return { valid: saved === state, redirect: null };
  }
  const nonce = state.slice(0, dotIndex);
  try {
    const redirect = atob(state.slice(dotIndex + 1));
    return { valid: saved === nonce, redirect };
  } catch {
    return { valid: false, redirect: null };
  }
}

export function buildLineAuthUrl(
  channelId: string,
  callbackUrl: string,
  redirect?: string,
): string {
  const state = generateState(redirect);
  const params = new URLSearchParams({
    response_type: "code",
    client_id: channelId,
    redirect_uri: callbackUrl,
    state,
    scope: "profile openid email",
    bot_prompt: "aggressive",
  });
  return `https://access.line.me/oauth2/v2.1/authorize?${params.toString()}`;
}

export async function loginWithFirebaseToken(idToken: string): Promise<{
  userId: string;
  token: string;
  isNewUser: boolean;
}> {
  const res = await fetch("/api/v0/auth/firebase", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id_token: idToken }),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? "Firebase認証に失敗しました");
  }

  return res.json();
}
